/**
 * Shop the Collection Drawer
 * Opens the shop-the-collection-drawer when the trigger button is clicked
 */

(function() {
  'use strict';

  const TRIGGER_SELECTOR = '.shop-the-collection__trigger';
  const DRAWER_SELECTOR = '#shop-the-collection-drawer';
  const CLOSE_BUTTON_SELECTOR = '[data-shop-the-collection-drawer-close]';
  const UNDERLAY_SELECTOR = '[data-drawer-underlay]';
  const HEADER_SELECTOR = '.shop-the-collection-block--top.drawer__head';

  class ShopTheCollectionDrawer {
    constructor() {
      this.trigger = document.querySelector(TRIGGER_SELECTOR);
      this.drawer = document.querySelector(DRAWER_SELECTOR);
      this.closeButton = this.drawer?.querySelector(CLOSE_BUTTON_SELECTOR);
      this.underlay = this.drawer?.querySelector(UNDERLAY_SELECTOR);
      this.drawerInner = this.drawer?.querySelector('[data-shop-the-collection-drawer-inner]');
      this.header = this.drawer?.querySelector(HEADER_SELECTOR);

      if (!this.trigger || !this.drawer) return;

      this.dragStartY = 0;
      this.currentY = 0;
      this.isDragging = false;
      this.isClosing = false;

      this.init();
    }

    init() {
      this.trigger.addEventListener('click', () => this.open());
      this.closeButton?.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      });
      this.underlay?.addEventListener('click', () => this.close());

      // Add drag-to-close for mobile
      if (this.header) {
        // Touch events
        this.header.addEventListener('touchstart', (e) => this.onDragStart(e), false);
        this.header.addEventListener('touchmove', (e) => this.onDragMove(e), false);
        this.header.addEventListener('touchend', (e) => this.onDragEnd(e), false);
        
        // Mouse events
        this.header.addEventListener('mousedown', (e) => this.onDragStart(e), false);
      }

      // Listen for form submission and close drawer
      const carousel = this.drawer?.querySelector('shop-the-collection-carousel');
      if (carousel) {
        carousel.addEventListener('shop-the-collection:ready', () => {
          this.setupFormSubmissionListener();
        });

        carousel.addEventListener('shop-the-collection:product-changed', () => {
          this.setupFormSubmissionListener();
        });

        this.setupFormSubmissionListener();
      }

      // Close on Escape key
      this.handleKeydown = (e) => {
        if (e.key === 'Escape' && this.drawer && this.drawer.classList.contains('is-visible')) {
          this.close();
        }
      };
      document.addEventListener('keydown', this.handleKeydown);
    }

    onDragStart(e) {
      // Only start drag on header
      if (e.target.closest('.shop-the-collection-block--top') === null) return;
      if (!this.drawer.classList.contains('is-visible')) return;
      
      this.isDragging = true;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      this.dragStartY = clientY;
      this.currentY = 0;
      
      // Prevent text selection while dragging
      if (e.type === 'mousedown') {
        e.preventDefault();
      }
      
      // Attach document listeners for mouse drag
      if (e.type === 'mousedown') {
        document.addEventListener('mousemove', this.onDragMove);
        document.addEventListener('mouseup', this.onDragEnd);
      }
    }

    onDragMove = (e) => {
      if (!this.isDragging || !this.drawer.classList.contains('is-visible')) return;
      
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      this.currentY = clientY - this.dragStartY;
      
      // Only allow dragging downward
      if (this.currentY > 0) {
        this.drawerInner.style.transition = 'none';
        this.drawerInner.style.transform = `translateY(${this.currentY}px)`;
      }
    }

    onDragEnd = (e) => {
      if (!this.isDragging) return;
      this.isDragging = false;
      
      // Remove document listeners for mouse drag
      document.removeEventListener('mousemove', this.onDragMove);
      document.removeEventListener('mouseup', this.onDragEnd);

      // If dragged down more than 50px, close the drawer
      if (this.currentY > 50) {
        this.drawerInner.style.transition = '';
        this.close();
      } else {
        // Otherwise, snap back with animation
        this.drawerInner.style.transition = 'transform 0.3s ease';
        this.drawerInner.style.transform = '';
        setTimeout(() => {
          this.drawerInner.style.transition = '';
        }, 300);
      }
      
      this.currentY = 0;
    }

    setupFormSubmissionListener() {
      const form = this.drawer?.querySelector('.shop-the-collection__product-form');
      if (!form || form._cartAddListenerAttached) return;

      form._cartAddListenerAttached = true;

      // Watch for the dynamically injected button and attach a click listener
      const buttonsContainer = this.drawer?.querySelector('[data-shop-the-collection-buttons]');
      if (buttonsContainer) {
        // Create observer to detect when button is added
        const observer = new MutationObserver((mutations) => {
          const button = buttonsContainer.querySelector('button[type="submit"]');
          if (button && !button._cartListenerAttached) {
            button._cartListenerAttached = true;
            
            // Attach click listener
            button.addEventListener('click', () => {
              // Close drawer after a small delay to allow form to process
              setTimeout(() => this.close(), 100);
            });
            
            // No longer need to observe
            observer.disconnect();
          }
        });

        // Check if button already exists
        const existingButton = buttonsContainer.querySelector('button[type="submit"]');
        if (existingButton && !existingButton._cartListenerAttached) {
          existingButton._cartListenerAttached = true;
          existingButton.addEventListener('click', () => {
            setTimeout(() => this.close(), 100);
          });
        } else {
          // Start observing if button doesn't exist yet
          observer.observe(buttonsContainer, {
            childList: true,
            subtree: true
          });
        }
      }
    }

    open() {
      this.drawer.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
      // document.body.style.paddingRight = '15px';
      this.trigger.setAttribute('aria-expanded', 'true');
      
      // Make all non-disabled radio labels focusable
      const radioLabels = this.drawer.querySelectorAll('.radio__fieldset .radio__button label');
      radioLabels.forEach(label => {
        const input = label.previousElementSibling;
        if (input && !input.disabled && !input.classList.contains('sold-out') && !input.classList.contains('unavailable')) {
          if (!label.hasAttribute('tabindex')) {
            label.setAttribute('tabindex', '0');
          }
        }
      });
      
      // Enable focus trap if theme supports it
      // if (window.theme?.a11y?.trapFocus) {
      //   window.theme.a11y.trapFocus(this.drawer);
      // }
    }

    close() {
      if (!this.drawer.classList.contains('is-visible') || this.isClosing) return;

      this.isClosing = true;
      this.drawer.classList.add('is-closing');
      this.drawer.classList.remove('is-visible');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      this.trigger.setAttribute('aria-expanded', 'false');
      
      // Remove focus trap if theme supports it
      // if (window.theme?.a11y?.removeTrapFocus) {
      //   window.theme.a11y.removeTrapFocus();
      // }

      window.theme.waitForAnimationEnd(this.drawerInner).then(() => {
        this.drawer.classList.remove('is-closing');
        this.drawerInner.style.transform = '';
        this.drawerInner.style.transition = '';
        this.isClosing = false;
        
        // Return focus to the trigger button
        this.trigger.focus();
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new ShopTheCollectionDrawer();
    });
  } else {
    new ShopTheCollectionDrawer();
  }
})();
